

































































































































































































































































































































































































































































































import { Component, Vue, Prop, Ref } from 'vue-property-decorator'
import * as db from '@shared/types/database'
import { DuplicateCountMap } from '@/types/frontend'
import Attach, { Mode as AttachMode } from '@/components/Attach/Attach.vue'
import AttachButton from '@/components/Attach/AttachButton.vue'
import Redactor from '@/components/Redactor.vue'
import { Icon, Button, Select, SelectOption } from '@alpha/ink'
import QuestionHeader from '@/components/QuestionHeader.vue'
import QuestionWrapper from '@/components/QuestionWrapper.vue'
import A from '@/utilities/alpha'
import { questionTypeDisplayName, getLabelParts } from '@/utilities/template-helpers'

import ConfirmationBubble from '@/components/ConfirmationBubble.vue'

@Component({
  components: {
    Icon,
    Attach,
    AttachButton,
    Redactor,
    QuestionHeader,
    QuestionWrapper,
    Select,
    Button,
    ConfirmationBubble
  }
})
export default class TemplateQuestion extends Vue {
  @Prop() index!: number
  @Prop() question!: db.Question
  @Prop({ default: false }) showWarningStyles!: boolean
  @Prop({ default: true }) showErrorStyles!: boolean
  @Prop({ default: () => ({}) }) editableFields!: object
  @Prop({ default: false }) deletable!: boolean
  @Prop({ default: true }) editMode!: boolean
  @Prop({ default: () => ({}) }) assets!: db.TestPayload['prototypes']
  @Prop({ required: true }) templateFieldValues!: Record<string, db.TemplateValue>
  @Prop({ required: true }) templateFields!: Record<string, db.TemplateField>
  @Prop({ required: true }) templateQuestions!: db.Question[]
  @Prop({ required: true }) canModifyTemplateQuestions!: boolean
  @Ref('compInputs') readonly compInputs!: HTMLElement[]
  @Ref('input') readonly input!: HTMLElement | null

  deleteQuestionPrompt: boolean = false
  editQuestionPrompt: boolean = false
  validated: boolean = false
  inputValidation: any = this.validateInputs()
  currentEventListener: null | any = null
  appendedAssetButtonClicked: boolean = false
  variantsClicked:string[] = []

  get editableQuestion () {
    return !this.question.required && !this.question.uneditable
  }

  get tags () {
    const tags = []
    if (this.question.custom) {
      tags.push('Custom Question')
    }
    if (this.question.edited) {
      tags.push('Edited')
    }
    tags.push(questionTypeDisplayName(this.question))
    return tags
  }

  get AttachMode () {
    return AttachMode
  }

  get questionIsMovableUp () {
    return this.getQuestionIdx(this.question.id) !== 0
  }

  get questionIsMovableDown () {
    return (this.getQuestionIdx(this.question.id) + 1) !== this.templateQuestions.length
  }

  get labelParts () {
    return getLabelParts(this.question, this.templateFields)
  }

  get prependedField () {
    return this.getInputFieldByPlacement('prepend')
  }

  get appendedField () {
    return this.getInputFieldByPlacement('append')
  }

  get minRatingField () {
    return this.getInputFieldByPlacement('min-rating')
  }

  get minRatingFieldOptions (): SelectOption[] {
    if (!this.minRatingField?.options) return []
    return this.minRatingField.options.map(option => ({
      label: option,
      value: option
    }))
  }

  get minRatingFieldValue (): SelectOption | undefined {
    if (!this.minRatingField?.id) return
    const val = this.templateFieldValues[this.minRatingField.id].value as string
    return {
      label: val,
      value: val
    }
  }

  get maxRatingField () {
    return this.getInputFieldByPlacement('max-rating')
  }

  get maxRatingFieldOptions (): SelectOption[] {
    if (!this.maxRatingField?.options) return []
    return this.maxRatingField.options.map(option => ({
      label: option,
      value: option
    }))
  }

  get maxRatingFieldValue (): SelectOption | undefined {
    if (!this.maxRatingField?.id) return
    const val = this.templateFieldValues[this.maxRatingField.id].value as string
    return {
      label: val,
      value: val
    }
  }

  get minLabelField () {
    return this.getInputFieldByPlacement('min-label')
  }

  get maxLabelField () {
    return this.getInputFieldByPlacement('max-label')
  }

  get hasAppendedAsset () {
    return this.appendedField && this.appendedField.type === 'asset' && this.canAttachAsset
  }

  get showAppendedAsset () {
    if (!this.appendedField) return false
    if (this.appendedField.type !== 'asset') return false
    const validAppendFieldValue = this.appendedField.value !== undefined && this.appendedField.value !== null
    return this.canAttachAsset && (validAppendFieldValue || this.appendedAssetButtonClicked)
  }

  get hasInlineFields () {
    if ('templateFields' in this.question) {
      return this.question.templateFields.some((field: db.TemplateField) => field.placement === 'inline')
    } else {
      return false
    }
  }

  get duplicateComparisonOptions () {
    if (this.appendedField && this.appendedField.type === 'list' && this.appendedField.value) {
      const dupCheck = (this.appendedField.value as string[]).reduce((acc, value: string) => {
        if (acc[value]) {
          acc.duplicates++
        } else {
          acc[value] = true
        }
        return acc
      }, { duplicates: 0 } as DuplicateCountMap)
      return dupCheck.duplicates > 0
    } else {
      return false
    }
  }

  get isSplitTest () {
    if (!this.templateFieldValues) return false
    return Object.keys(this.templateFieldValues).reduce((acc, field) => {
      if (this.templateFieldValues[field].variant) acc = true
      return acc
    }, false)
  }

  get variants () {
    return Object.keys(this.templateFieldValues).reduce((acc:any, field) => {
      if (this.templateFieldValues[field].variant && field.split('-').includes('variant')) {
        const variant = {
          id: field,
          ...this.templateFieldValues[field]
        }
        acc.push(variant)
      }
      return acc
    }, [])
  }

  get requiresAttachment () {
    if (!('templateFields' in this.question)) return false
    return this.question.templateFields.reduce((acc, field: db.TemplateField) => {
      const id = field.id
      if (id) {
        const fieldKeys = Object.keys(this.templateFields[id])
        if (fieldKeys.includes('requiredAssetType')) acc = true
      }
      return acc
    }, false)
  }

  get canAttachAsset () {
    const templateFields = Object.values(this.templateFields)
    const hasAssetTemplateField = !!templateFields.find((templateField) => templateField.type === 'asset')
    return hasAssetTemplateField && (!!this.question.required || this.question.id === 'click_expectations_6')
  }

  get questionSpecificTemplateFieldValues () {
    const tfvObj: any = {}
    if ('templateFields' in this.question) {
      this.question.templateFields.forEach(({ id, placement }) => {
        if (!id || placement === 'prepend' || placement === 'append') return
        tfvObj[id] = { ...this.templateFieldValues[id], placement }
      })
    }
    return tfvObj
  }

  getTemplateFieldValues (question: db.QuestionWithFields) {
    return question.templateFields.map(({ id }) => {
      if (id) return this.templateFieldValues[id]
    })
  }

  addSplitTestOption (question: db.Question) {
    this.$emit('addSplitTestOption', question)
  }

  toggleAttachmentComp (variantId: string) {
    if (!this.templateFieldValues[variantId].asset) {
      if (this.variantsClicked.includes(variantId)) {
        const removedVariant = this.variantsClicked.filter(variant => variant !== variantId)
        this.variantsClicked = removedVariant
      } else {
        this.variantsClicked = this.variantsClicked.concat([variantId])
      }
    }
  }

  deleteVariant (variantId: string) {
    this.$emit('deleteVariant', variantId)
  }

  setVariantLabel (variantId: string, value: string) {
    this.$emit('setVariantLabel', { variantId, value })
  }

  getQuestionIdx (id: string) {
    return this.templateQuestions.map(q => q.id).indexOf(id)
  }

  // TODO:Harry unify the logic for delete and edit prompts
  switchEditPrompt ($event: Event) {
    $event.stopPropagation()
    this.editQuestionPrompt = true
    const closeEditDropdown = (event: Event) => {
      if (this.$refs.editIcon !== event.currentTarget) {
        this.editQuestionPrompt = false
        document.removeEventListener('click', closeEditDropdown)
      }
    }
    document.addEventListener('click', closeEditDropdown.bind(this))
  }

  switchDeletePrompt ($event: Event) {
    $event.stopPropagation()
    this.deleteQuestionPrompt = true
    const closeDeleteDropdown = (event: Event) => {
      if (this.$refs.deleteIcon !== event.currentTarget) {
        this.deleteQuestionPrompt = false
        document.removeEventListener('click', closeDeleteDropdown)
      }
    }
    document.addEventListener('click', closeDeleteDropdown.bind(this))
  }

  getInputFieldByPlacement (placement: db.TemplateField['placement']): db.TemplateField | undefined {
    if (!('templateFields' in this.question)) return
    const field = this.question.templateFields.find((field: db.TemplateField) => {
      return field.placement === placement
    })
    if (!field || !field.id) return
    return {
      ...this.templateFields[field.id],
      value: this.templateFieldValues[field.id] ? this.templateFieldValues[field.id].value : [''],
      id: field.id
    }
  }

  validateInputs () {
    // TODO:COLLIN figure out how to actually type this.. can't do Element[] can't do Array<Element | typeof Redactor>.. pretty much stuck
    let inputs = this.$refs.input as any[]
    const compInputs = this.compInputs
    // Make sure comparison items get validated as well
    if (compInputs) {
      if (!inputs) {
        inputs = compInputs
      } else {
        inputs = inputs.concat(compInputs)
      }
    }
    if (!inputs) return {}
    if (this.$refs.minLabel && this.$refs.maxLabel) {
      inputs.push(this.$refs.minLabel)
      inputs.push(this.$refs.maxLabel)
    }
    const validation = inputs.reduce((acc, input) => {
      let value
      if (input.object) {
        value = input.object[input.field]
      } else {
        value = input.value
      }
      const validationId = input.$attrs ? input.$attrs['data-validation-id'] : input.getAttribute('data-validation-id')
      acc[validationId] = !!A.stripHTML(value).trim() && this.notPlaceholderIfEdited()
      return acc
    }, {})
    this.inputValidation = validation
  }

  validate () {
    this.validated = true
    this.validateInputs()
    const inputsValid = this.inputsValid()
    const attachedComponentsValid = this.validateAttachedComponents()
    const noDuplicateComparison = !this.duplicateComparisonOptions
    return inputsValid && attachedComponentsValid && noDuplicateComparison
  }

  clearValidation () {
    this.validated = false
  }

  notPlaceholderIfEdited () {
    let valid = true
    if (this.question.edited && 'templateFields' in this.question) {
      const questionLabelInput = this.question.templateFields.find((templateField) => templateField.placement === 'inline' && templateField.type === 'textarea')
      if (!questionLabelInput) return valid
      if (questionLabelInput.id === undefined) return true
      const questionLabel = this.templateFieldValues[questionLabelInput.id].value
      if (typeof questionLabel !== 'string') return valid
      let seenOpeningBracket = false
      for (let i = 0; i < questionLabel.length; i++) {
        const letter = questionLabel[i]
        if (letter === '[') seenOpeningBracket = true
        if (letter === ']' && seenOpeningBracket) valid = false
      }
    }
    return valid
  }

  inputsValid () {
    return Object.values(this.inputValidation).every(value => !!value)
  }

  validateAttachedComponents () {
    const attachComponents = this.$refs.attachComponents
    let attachComponentsArray
    if (!attachComponents && this.requiresAttachment) return false
    // If split test is activated and the core question requires an asset then each variant must have an asset
    if (this.isSplitTest && this.requiresAttachment) {
      const allVariantsHaveAttachment = Object.keys(this.templateFieldValues).reduce((acc, key) => {
        const value = this.templateFieldValues[key]
        const valueIncludesAsset = Object.keys(value).includes('asset')
        if (value.variant && valueIncludesAsset && !value.asset) acc = false
        return acc
      }, true)
      if (!allVariantsHaveAttachment) return false
    }
    if (!attachComponents) return true
    if (!Array.isArray(attachComponents)) {
      attachComponentsArray = [attachComponents]
    } else {
      attachComponentsArray = attachComponents
    }
    const valid = (attachComponentsArray as Attach[]).map((attach: Attach) => {
      return attach.validate()
    })
    return valid.every(value => {
      // Because the id can be 0 which would return falsy check and cause validation to trigger even if a asset has been attached
      if (value !== null && !value) {
        return true
      } else {
        return value
      }
    })
  }

  addComparisonOption (payload: { id: string, value: string, idx: number }) {
    const { idx } = payload
    // @ts-ignore I can't force ts lint to understand that this active element does in fact have a dataset with an index
    const focusedInputIndex: number = parseInt(document.activeElement.dataset.idx)
    const lastIndex = (idx - 1)
    if (Number.isNaN(focusedInputIndex) || focusedInputIndex === lastIndex) {
      // User clicked add new option or is currently focused on the last option in the list
      this.updateValue(payload)
      this.$nextTick(() => {
        const element = this.compInputs[idx]
        element.focus()
      })
    } else {
      // Focus the next input in the list
      if (this.compInputs) {
        const inputToFocus = this.compInputs[focusedInputIndex + 1]
        inputToFocus.focus()
      }
    }
  }

  updateValue (payload: { id: string, value: any, idx?: number, [x: string]: any }) {
    this.validated = false
    payload.nonTemplateQuestion = this.question.edited || this.question.custom
    payload.questionId = this.question.id
    return this.updateInputValue(payload)
  }

  toggleLockAnswers (id: string) {
    this.templateFieldValues[id].lockOptions = !this.templateFieldValues[id].lockOptions
    const lockValue = this.templateFieldValues[id].lockOptions
    this.$emit('updateLockValue', { id, lockValue })
  }

  newAsset (payload: { fieldId: string, asset: string }) {
    const { fieldId, asset } = payload
    const assetId = new Date().getTime().toString()
    this.$emit('addAssetToTest', { asset, assetId })
    this.updateValue({ id: fieldId, value: assetId })
  }

  newVariantAsset (payload: { fieldId: string, asset: string }) {
    const { fieldId, asset } = payload
    const assetId = new Date().getTime().toString()
    this.$emit('addAssetToTest', { asset, assetId })
    this.$emit('updateVariantAsset', { id: fieldId, asset: assetId })
  }

  selectAsset (payload: { fieldId: string, assetId: string }) {
    const { fieldId, assetId } = payload
    const asset = this.assets[assetId]
    this.$emit('addAssetToTest', { asset, assetId })
    this.updateValue({ id: fieldId, value: assetId })
  }

  selectVariantAsset (payload: { fieldId: string, assetId: string }) {
    const { fieldId, assetId } = payload
    const asset = this.assets[assetId]
    this.$emit('addAssetToTest', { asset, assetId })
    this.$emit('updateVariantAsset', { id: fieldId, asset: assetId })
  }

  removeAsset (payload: { fieldId: string, assetId: string }) {
    const { fieldId, assetId } = payload
    this.$emit('removeAssetFromTest', { assetId })
    this.updateValue({ id: fieldId, value: null })
  }

  removeVariantAsset (payload: { fieldId: string, assetId: string }) {
    const { fieldId, assetId } = payload
    this.$emit('removeAssetFromTest', { assetId })
    this.$emit('updateVariantAsset', { id: fieldId, asset: null })
  }

  updateInputValue (payload: { id: string, value: any }) {
    this.$emit('updateInputValue', payload)
  }

  editQuestion () {
    this.$emit('editQuestion', this.question.id)
  }

  deleteQuestion () {
    this.$emit('deleteQuestion', this.question.id)
  }

  deleteInputValue (payload: any) {
    this.$emit('deleteInputValue', payload)
  }

  reorderQuestion (payload: any) {
    this.$emit('reorderQuestion', payload)
  }

  setQuestionOption (payload: any) {
    this.$emit('setQuestionOption', payload)
  }
}
